import classNames from "classnames";
import styles from "./AccountsList.module.scss";
import { useEffect, useState } from "react";
import { Button } from "@/components";
import { Account } from "../../_types/accounts.types";
import { AccountListItem } from "../account-list-item";
import { AccountDetailsDrawer } from "../account-details-drawer";

type AccountsListProps = {
  accounts: Array<Account>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  currentUserId: string | null;
  actionsDisabled: boolean;
  onRequestDelete: (account: Account) => void;
};

function isDeleteDisabled(account: Account, currentUserId: string | null) {
  return currentUserId != null && account.id === currentUserId;
}

export const AccountsList = ({
  accounts,
  isLoading,
  isFetching,
  isError,
  onRetry,
  currentUserId,
  actionsDisabled,
  onRequestDelete,
}: AccountsListProps) => {
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  useEffect(() => {
    if (!selectedAccount) return;

    const nextSelected =
      accounts.find((account) => account.id === selectedAccount.id) ?? null;

    if (nextSelected) {
      setSelectedAccount(nextSelected);
    } else {
      setSelectedAccount(null);
    }
  }, [accounts, selectedAccount]);

  if (isLoading) {
    return (
      <div className={styles["comp__state"]}>
        <div className={styles["comp__spinner"]} />
        <div>Loading accounts…</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles["comp__state"]}>
        <div>Could not load accounts.</div>
        <Button size="small" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (accounts.length === 0) {
    return <div className={styles["comp__state"]}>No accounts found.</div>;
  }

  return (
    <>
      <div className={styles["comp"]} aria-busy={isFetching}>
        <div className={styles["comp__table-header"]}>
          <div className={styles["comp__column"]}>Name &amp; Email</div>
          <div className={styles["comp__column"]}>Role</div>
          <div className={styles["comp__column"]}>Subscription</div>
          <div className={styles["comp__column"]}>Created at</div>
          <div className={styles["comp__column"]}>Last activity</div>
          <div className={classNames(styles["comp__column"], styles["comp__column--actions"])}>
            Actions
          </div>
        </div>

        <div className={styles["comp__list"]}>
          {accounts.map((account) => (
            <AccountListItem
              key={account.id}
              account={account}
              onOpenDetails={() => setSelectedAccount(account)}
              onEdit={() => setSelectedAccount(account)}
              onRequestDelete={() => onRequestDelete(account)}
              actionsDisabled={actionsDisabled}
              deleteDisabled={isDeleteDisabled(account, currentUserId)}
            />
          ))}
        </div>
      </div>

      <AccountDetailsDrawer
        account={selectedAccount}
        isOpen={selectedAccount !== null}
        onClose={() => setSelectedAccount(null)}
      />
    </>
  );
};
